
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import OrderItems from '../../order-confirmation/components/OrderItems';
import OrderSummaryCard from '../../order-confirmation/components/OrderSummaryCard';
import { EstimatedDeliveryBadge } from '../../../components/feature/EstimatedDelivery';
import CarrierTracking from './CarrierTracking';
import LiveTrackingTimeline from './LiveTrackingTimeline';
import TrackingProgressMap from './TrackingProgressMap';
import { supabase } from '../../../lib/supabase';
import type { TrackingEvent } from './LiveTrackingTimeline';

interface OrderData {
  id: string;
  customer_email: string;
  customer_first_name: string;
  customer_last_name: string;
  shipping_address: string;
  shipping_city: string;
  shipping_state: string;
  shipping_zip: string;
  shipping_country: string;
  shipping_method?: string;
  carrier?: string;
  tracking_number?: string;
  tracking_events?: TrackingEvent[];
  items: Array<{
    product_id: number;
    quantity: number;
    unit_price: number;
    name?: string;
    image?: string;
  }>;
  subtotal: number;
  shipping_cost: number;
  tax: number;
  discount: number;
  total: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface TrackingResultProps {
  order: OrderData;
  onReset: () => void;
}

const statusLabels: Record<string, { label: string; color: string; icon: string }> = {
  pending: { label: 'En attente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-time-line' },
  processing: { label: 'En préparation', color: 'bg-sky-50 text-sky-700 border-sky-200', icon: 'ri-settings-3-line' },
  shipped: { label: 'Expédiée', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: 'ri-truck-line' },
  delivered: { label: 'Livrée', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-check-double-line' },
  cancelled: { label: 'Annulée', color: 'bg-red-50 text-red-700 border-red-200', icon: 'ri-close-circle-line' },
};

function generateDefaultEvents(order: OrderData): TrackingEvent[] {
  const events: TrackingEvent[] = [];
  const statuses = ['pending', 'processing', 'shipped', 'delivered'];
  const currentIdx = statuses.indexOf(order.status);

  const orderDate = new Date(order.created_at);

  events.push({
    id: 'evt-1',
    status: 'order_placed',
    title: 'Commande confirmée',
    description: 'Votre commande a été reçue et confirmée avec succès.',
    timestamp: orderDate.toISOString(),
  });

  if (currentIdx >= 1) {
    const processingDate = new Date(orderDate);
    processingDate.setHours(processingDate.getHours() + 2);
    events.push({
      id: 'evt-2',
      status: 'payment_confirmed',
      title: 'Paiement validé',
      description: 'Le paiement a été vérifié et accepté.',
      timestamp: processingDate.toISOString(),
    });

    const prepDate = new Date(orderDate);
    prepDate.setHours(prepDate.getHours() + 4);
    events.push({
      id: 'evt-3',
      status: 'processing',
      title: 'Préparation en cours',
      description: 'Votre commande est en cours de préparation par notre équipe.',
      timestamp: prepDate.toISOString(),
    });

    const qualityDate = new Date(orderDate);
    qualityDate.setHours(qualityDate.getHours() + 8);
    events.push({
      id: 'evt-4',
      status: 'quality_check',
      title: 'Contrôle qualité',
      description: 'Vos produits ont passé notre contrôle qualité avec succès.',
      timestamp: qualityDate.toISOString(),
    });
  }

  if (currentIdx >= 2) {
    const packedDate = new Date(orderDate);
    packedDate.setDate(packedDate.getDate() + 1);
    events.push({
      id: 'evt-5',
      status: 'packed',
      title: 'Colis emballé',
      description: 'Votre commande a été soigneusement emballée.',
      timestamp: packedDate.toISOString(),
    });

    const shippedDate = new Date(orderDate);
    shippedDate.setDate(shippedDate.getDate() + 1);
    shippedDate.setHours(shippedDate.getHours() + 6);
    events.push({
      id: 'evt-6',
      status: 'shipped',
      title: 'Colis expédié',
      description: `Votre colis a été remis au transporteur${order.carrier ? ` ${order.carrier.toUpperCase()}` : ''}.`,
      location: 'Centre de distribution',
      timestamp: shippedDate.toISOString(),
    });

    const transitDate = new Date(orderDate);
    transitDate.setDate(transitDate.getDate() + 2);
    events.push({
      id: 'evt-7',
      status: 'in_transit',
      title: 'En transit',
      description: 'Votre colis est en route vers sa destination.',
      location: 'Centre de tri régional',
      timestamp: transitDate.toISOString(),
    });
  }

  if (currentIdx >= 3) {
    const outDate = new Date(orderDate);
    outDate.setDate(outDate.getDate() + 3);
    events.push({
      id: 'evt-8',
      status: 'out_for_delivery',
      title: 'En cours de livraison',
      description: 'Votre colis est en cours de livraison à votre adresse.',
      location: `${order.shipping_city}, ${order.shipping_state}`,
      timestamp: outDate.toISOString(),
    });

    const deliveredDate = new Date(orderDate);
    deliveredDate.setDate(deliveredDate.getDate() + 3);
    deliveredDate.setHours(deliveredDate.getHours() + 4);
    events.push({
      id: 'evt-9',
      status: 'delivered',
      title: 'Livré avec succès',
      description: `Votre colis a été livré à ${order.shipping_address}.`,
      location: `${order.shipping_city}, ${order.shipping_state} ${order.shipping_zip}`,
      timestamp: deliveredDate.toISOString(),
    });
  }

  return events;
}

export default function TrackingResult({ order: initialOrder, onReset }: TrackingResultProps) {
  const [order, setOrder] = useState<OrderData>(initialOrder);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const statusInfo = statusLabels[order.status] ?? statusLabels.pending;

  const trackingEvents: TrackingEvent[] =
    order.tracking_events && order.tracking_events.length > 0
      ? order.tracking_events
      : generateDefaultEvents(order);

  const orderDate = (() => {
    try {
      return new Date(order.created_at).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Date invalide';
    }
  })();

  const refreshOrder = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', order.id)
        .maybeSingle();

      if (!error && data) {
        setOrder(data);
      }
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [order.id]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh || order.status === 'delivered' || order.status === 'cancelled') return;

    const interval = setInterval(() => {
      refreshOrder();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, order.status, refreshOrder]);

  // Supabase realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel(`order-${order.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${order.id}`,
        },
        (payload) => {
          if (payload.new) {
            setOrder(payload.new as OrderData);
            setLastRefresh(new Date());
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [order.id]);

  return (
    <section className="max-w-5xl mx-auto px-6 py-12">
      {/* Back / Reset + Refresh controls */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={onReset}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#2E7D32] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-lg"></i>
          Suivre une autre commande
        </button>

        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              autoRefresh
                ? 'bg-[#E8F5E9] text-[#2E7D32] border border-[#C8E6C9]'
                : 'bg-gray-100 text-gray-500 border border-gray-200'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                autoRefresh ? 'bg-[#2E7D32] animate-pulse' : 'bg-gray-400'
              }`}
            ></span>
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>

          {/* Manual refresh */}
          <button
            onClick={refreshOrder}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line text-sm ${isRefreshing ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>

          {/* Last refresh time */}
          <span className="text-[10px] text-gray-400">
            Mis à jour :{' '}
            {lastRefresh.toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        </div>
      </div>

      {/* Order Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-xs text-gray-400 tracking-widest font-medium mb-1">NUMÉRO DE COMMANDE</p>
            <div className="flex items-center gap-2">
              <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-1.5 rounded-lg">
                {order.id?.substring(0, 8).toUpperCase() ?? 'N/A'}
              </p>
              <button
                onClick={() => {
                  if (order.id) navigator.clipboard.writeText(order.id).catch(() => {});
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#2E7D32] hover:bg-[#E8F5E9] rounded-lg transition-colors cursor-pointer"
                title="Copier l'ID complet"
              >
                <i className="ri-file-copy-line text-sm"></i>
              </button>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 tracking-widest font-medium mb-1">STATUT</p>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border ${statusInfo.color}`}>
              <i className={`${statusInfo.icon} text-sm`}></i>
              {statusInfo.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 text-sm">
          <div>
            <p className="text-xs text-gray-400 mb-1">Client</p>
            <p className="text-gray-900 font-medium">
              {order.customer_first_name} {order.customer_last_name}
            </p>
            <p className="text-gray-500 text-xs mt-0.5">{order.customer_email}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Date de commande</p>
            <p className="text-gray-900 font-medium">{orderDate}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Livrer à</p>
            <p className="text-gray-900 font-medium">{order.shipping_address}</p>
            <p className="text-gray-500 text-xs mt-0.5">
              {order.shipping_city}, {order.shipping_state} {order.shipping_zip}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Map */}
      <div className="mb-6">
        <TrackingProgressMap
          currentStatus={order.status}
          shippingMethod={order.shipping_method}
          orderDate={order.created_at}
          carrier={order.carrier}
        />
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-5 gap-6">
        <div className="col-span-3 space-y-6">
          {/* Live Tracking Timeline */}
          <LiveTrackingTimeline events={trackingEvents} currentStatus={order.status} />

          {/* Carrier Tracking */}
          <CarrierTracking
            orderStatus={order.status}
            carrierId={order.carrier?.toLowerCase()}
            trackingNumber={order.tracking_number}
          />

          {/* Items */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider mb-4">
              ARTICLES COMMANDÉS ({order.items.length})
            </h3>
            <OrderItems items={order.items} />
          </div>
        </div>

        <div className="col-span-2 space-y-6">
          {/* Estimated delivery */}
          {order.status !== 'cancelled' && (
            <EstimatedDeliveryBadge
              shippingMethodId={order.shipping_method || 'standard'}
              orderDate={order.created_at}
            />
          )}

          <OrderSummaryCard
            subtotal={order.subtotal}
            shippingCost={order.shipping_cost}
            tax={order.tax}
            discount={order.discount}
            total={order.total}
          />

          {/* Notifications card */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider mb-3">NOTIFICATIONS</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-[#FAFDF7] rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-check-line text-sm text-[#2E7D32]"></i>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-800">Email de confirmation</p>
                  <p className="text-[10px] text-gray-400">Envoyé à {order.customer_email}</p>
                </div>
              </div>
              {(order.status === 'shipped' || order.status === 'delivered') && (
                <div className="flex items-center gap-3 p-3 bg-[#FAFDF7] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                    <i className="ri-truck-line text-sm text-[#2E7D32]"></i>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-800">Email d'expédition</p>
                    <p className="text-[10px] text-gray-400">Avec numéro de suivi</p>
                  </div>
                </div>
              )}
              {order.status === 'delivered' && (
                <div className="flex items-center gap-3 p-3 bg-[#FAFDF7] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-smile-line text-sm text-[#2E7D32]"></i>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-800">Email de livraison</p>
                    <p className="text-[10px] text-gray-400">Confirmation de réception</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Help card */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider mb-3">BESOIN D'AIDE ?</h3>
            <p className="text-xs text-gray-500 mb-4">
              Des questions sur votre commande ? Nous sommes là pour vous aider.
            </p>
            <a
              href="mailto:support@daliahsecret.com"
              className="flex items-center gap-2 text-sm text-[#2E7D32] font-medium hover:underline cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              support@daliahsecret.com
            </a>
            <Link
              to="/shipping-returns"
              className="flex items-center gap-2 text-sm text-[#2E7D32] font-medium hover:underline cursor-pointer mt-2"
            >
              <i className="ri-truck-line"></i>
              Politique de livraison &amp; retours
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
